import getSymbolFromCurrency from 'currency-symbol-map';
import { snakeCase, pick, orderBy } from 'lodash';
import paths from 'deepdash/paths';
import { dayjs } from '@koob/dates';
import Dinero from 'dinero.js';
import hresa from '../images/bedBanks/h-resa.png';
import expedia from '../images/bedBanks/expedia.png';
import dmc from '../images/bedBanks/dmc.png';

export const formatImageLinkForCss = (link) => {
  return "'" + encodeURI(link) + "'";
};

export const getYesterday = () =>
  dayjs().subtract(1, 'day').startOf('day').toDate();

export const getFirstAndLastDayOfMonth = () => {
  const date = new Date();
  return {
    first: dayjs(date).startOf('month').toDate(),
    last: dayjs(date).endOf('month').toDate(),
  };
};

export const getOneYearPeriod = () => {
  const date = new Date();
  return { first: dayjs(date).add(-1, 'year').toDate(), last: date };
};

export const remove = (list, itemValue) =>
  list.filter((item) => item.value !== itemValue);

export const initialLocale = {
  value: 'en',
  label: 'EN',
  alpha2: 'gb',
};

export const getRole = (role) => {
  switch (role) {
    case 'koob_admin':
      return 'user:roles.koobAdmin';
    case 'dmc_admin':
      return 'user:roles.dmcAdmin';
    case 'super_admin':
      return 'user:roles.superAdmin';
    case 'to_admin':
      return 'user:roles.toAdmin';
    case 'to_user':
      return 'user:roles.toUser';
    default:
      return 'user:roles.dmcUser';
  }
};

export const extractValues = (schema, values) => {
  const schemaPaths = paths(schema.default());
  return pick(values, schemaPaths);
};

export const constantCase = (input) => snakeCase(input).toUpperCase();

export const formatOrderCriteria = ({ order, direction }) => {
  if (!Boolean(order)) {
    return {
      column: 'UPDATED_AT',
      direction: 'DESC',
    };
  }

  return {
    column: constantCase(order),
    direction: constantCase(direction) || 'ASC',
  };
};

export const pluralize = (input, count) =>
  input
    .split(' ')
    .map((i) => `${i}${count > 1 ? 's' : ''}`)
    .join(' ');

export const readFile = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      resolve(reader.result);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const sortFacilitiesByGroup = (facilities) => {
  let result = {};

  facilities.forEach((facility) => {
    const groups = facility?.groups?.nodes || facility?.groups || [];
    groups.forEach((group) => {
      if (Boolean(result[group.id])) {
        result[group.id].facilities.push(facility);
      } else {
        result[group.id] = {
          group,
          facilities: [facility],
        };
      }
    });
  });
  return Object.values(result);
};
export const formatFacilitiesToGroupsFacilities = (
  facilities,
  othersLabel = 'Others',
) => {
  if (facilities?.length === 0) {
    return [];
  }

  const [facilitiesWithGroups, facilitiesWithoutGroups] = facilities.reduce(
    (acc, curr, index) => {
      const facility = { ...(curr?.node || curr) };
      const faciltyGroups = facility.groups?.nodes || facility.groups;
      const accIndex = faciltyGroups?.length > 0 ? 0 : 1;
      acc[accIndex].push({
        ...facility,
        index,
      });
      return acc;
    },
    [[], []],
  );

  const sortedFacilitiesByGroup = sortFacilitiesByGroup(facilitiesWithGroups);

  const reorderGroupsFacilities = orderBy(
    sortedFacilitiesByGroup,
    ['group.displayName'],
    ['asc'],
  );

  if (facilitiesWithoutGroups.length === 0) {
    return reorderGroupsFacilities;
  }

  const groupsFacilitiesAndOthers = reorderGroupsFacilities.concat([
    {
      group: { displayName: othersLabel, id: 'others' },
      facilities: facilitiesWithoutGroups,
    },
  ]);

  return groupsFacilitiesAndOthers;
};

export const getBedBankName = (code, organizationName = null) => {
  switch (code) {
    case 'HRL':
      return 'H-resa';
    case 'RXP':
      return 'Expedia';
    default:
      return organizationName === null || organizationName === 'null'
        ? 'DMC Contract'
        : organizationName;
  }
};

export const getBedBankLogo = (code, organizationLogo) => {
  switch (code) {
    case 'HRL':
      return hresa;
    case 'RXP':
      return expedia;
    default:
      return organizationLogo === null || organizationLogo === 'null'
        ? dmc
        : organizationLogo;
  }
};

const DEFAULT_CURRENCY_PRECISION = 2;

// Minor units of a currency (0 for VND or JPY, 3 for KWD), as known by Intl.
const getCurrencyPrecision = (currency) => {
  try {
    return new Intl.NumberFormat('en', {
      style: 'currency',
      currency,
    }).resolvedOptions().maximumFractionDigits;
  } catch (err) {
    return DEFAULT_CURRENCY_PRECISION;
  }
};

// `useCurrencyPrecision` rounds and prints the amount on the currency's own
// minor units instead of the historical two decimals.
export const formatCurrency = ({
  amount,
  currency,
  useCurrencyPrecision = false,
}) => {
  if (!amount && amount !== 0) {
    return '';
  }

  const precision = useCurrencyPrecision
    ? getCurrencyPrecision(currency)
    : DEFAULT_CURRENCY_PRECISION;

  try {
    return Dinero({
      amount: Math.round(amount * 10 ** precision),
      currency,
      precision,
    })
      .setLocale('fr')
      .toFormat(precision > 0 ? `$0,0.${'0'.repeat(precision)}` : '$0,0')
      .toUpperCase();
  } catch (err) {
    // An unknown or missing currency must still print a readable amount:
    // the raw float would leak its binary rounding (609.6800000000001).
    const numericAmount = Number(amount);

    return Number.isFinite(numericAmount) ? numericAmount.toFixed(2) : amount;
  }
};

export const phoneNumberRegex = /^[+]?[0-9]{10,15}$/;

export const formatAmount = ({ amount, currency = 'USD' }) =>
  new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency,
  }).format(amount);

export const formatCurrencyLabel = (currency) =>
  `${currency.toUpperCase()}  ${getSymbolFromCurrency(currency)}`;

export const getMostRecent = (array) => {
  if (!array || array.length === 0) {
    return null;
  }

  return array.reduce((mostRecent, current) => {
    const currentDate = new Date(current.createdAt);
    const mostRecentDate = new Date(mostRecent.createdAt);

    return currentDate > mostRecentDate ? current : mostRecent;
  });
};

export const settingRequestState = ['pending', 'refused'];

export const stateEnum = {
  PENDING: 'pending',
  CONFIRMED: 'confirmed',
  VALIDATED: 'validated',
  REFUSED: 'refused',
  CANCELED: 'canceled',
  SENT: 'sent',
  NOT_REQUESTED: 'not_requested',
  INSTANT_BOOKING: 'instant_booking',
  IN_PROGRESS: 'in_progress',
  STAND_BY: 'stand_by',
  ARCHIVED: 'archived',
};

export const viewKind = {
  LIST: 'list',
  MAP: 'map',
};

export const permissionsEnum = {
  READ_BOOKINGS: 'readBookings',
};

export const decodeId = (idKoob) => {
  if (!idKoob) {
    return null;
  }
  try {
    const result = parseInt(idKoob);
    if (!isNaN(result)) {
      return result;
    }
  } catch (e) {
    return null;
  }
  const decoded = atob(idKoob.replace('\n', ''));
  return parseInt(decoded.substring(decoded.lastIndexOf('/') + 1));
};

export const fluxesWithAllotment = {
  ISX: 'ISX',
  FEL: 'FEL',
  HRC: 'HRC',
  SGA: 'SGA',
  LHW: 'LHW',
  SLH: 'SLH',
  SNH: 'SNH',
  LCS: 'LCS',
};

export const roomTags = {
  REFUNDABLE: 'refundable',
  NON_REFUNDABLE: 'non_refundable',
};

export const koobRole = {
  KOOB_ADMIN: 'koob_admin',
  DMC_ADMIN: 'dmc_admin',
  DMC_USER: 'dmc_user',
};

export const ProductTypeEnum = {
  EXPERIENCE: 'experience',
  HOTEL: 'hotel',
  TRIP: 'trip',
  ALL: 'all',
};

export const parseBoolean = (value) => {
  if (value === 'true' || value === true) {
    return true;
  }

  if (value === 'false' || value === false) {
    return false;
  }

  return undefined;
};

export const locationKindEnum = {
  COUNTRY: 'country',
  REGION: 'region',
  CITY: 'city',
};

export const BenefitKind = {
  'Benefits::Amenity': 'amenity',
  'Benefits::Amount': 'amount',
  'Benefits::FreeNight': 'free_night',
  'Benefits::Percent': 'percent',
  'Benefits::UpgradeRoom': 'upgrade_room',
  'Benefits::Supplement': 'supplement',
  'Benefits::SupplementPercent': 'supplement_percent',
};

export const BenefitKindEnum = {
  amenity: 'Benefits::Amenity',
  amount: 'Benefits::Amount',
  free_night: 'Benefits::FreeNight',
  percent: 'Benefits::Percent',
  upgrade_room: 'Benefits::UpgradeRoom',
  supplement: 'Benefits::Supplement',
  supplement_percent: 'Benefits::SupplementPercent',
};

export const PromotionStateEnum = {
  OPERATIONAL: 'operational',
  DRAFT: 'draft',
  ARCHIVED: 'archived',
  EXPIRED: 'expired',
};

export const PromotionKindEnum = {
  AMENITY: 'amenity',
  AMOUNT: 'amount',
  FREE_NIGHT: 'free_night',
  PERCENT: 'percent',
  UPGRADE_ROOM: 'upgrade_room',
  SUPPLEMENT: 'supplement',
  SUPPLEMENT_PERCENT: 'supplement_percent',
};

export const searchLocationKindEnum = {
  COUNTRIES: 'countries',
  REGIONS: 'regions',
  CITIES: 'cities',
};

export const defaultWithBookingsBetween = {
  from: new Date('1900-01-01'),
  to: new Date(),
};

export const notificationTypesEnum = {
  BookingSentToPartnersRecapNotification:
    'BookingSentToPartnersRecapNotification',
  ExperienceBookingCancelledNotification:
    'ExperienceBookingCancelledNotification',
  ExperienceBookingConfirmedNotification:
    'ExperienceBookingConfirmedNotification',
  ExperienceBookingGdCancelledNotification:
    'ExperienceBookingGdCancelledNotification',
  ExperienceBookingGdConfirmedNotification:
    'ExperienceBookingGdConfirmedNotification',
  ExperienceBookingGdPreConfirmedNotification:
    'ExperienceBookingGdPreConfirmedNotification',
  ExperienceBookingGdRequestedNotification:
    'ExperienceBookingGdRequestedNotification',
  ExperienceBookingImportantUpdateNotification:
    'ExperienceBookingImportantUpdateNotification',
  ExperienceBookingRefusedNotification: 'ExperienceBookingRefusedNotification',
  ExperienceBookingRequestNotification: 'ExperienceBookingRequestNotification',
  ExperienceBookingUpdatedNotification: 'ExperienceBookingUpdatedNotification',
  HotelBookingCancelledNotification: 'HotelBookingCancelledNotification',
  HotelBookingConfirmedNotification: 'HotelBookingConfirmedNotification',
  HotelBookingImportantUpdateNotification:
    'HotelBookingImportantUpdateNotification',
  HotelBookingOtherProposalNotification:
    'HotelBookingOtherProposalNotification',
  HotelBookingRefusedNotification: 'HotelBookingRefusedNotification',
  HotelBookingRequestNotification: 'HotelBookingRequestNotification',
  HotelBookingUpdatedNotification: 'HotelBookingUpdatedNotification',
  ToConnectionAcceptedNotification: 'ToConnectionAcceptedNotification',
  ToConnectionRefusedNotification: 'ToConnectionRefusedNotification',
  ToConnectionRequestedNotification: 'ToConnectionRequestedNotification',
  TripAssistantAssignedNotification: 'TripAssistantAssignedNotification',
  TripBookingConfirmedNotification: 'TripBookingConfirmedNotification',
  TripHelpAnsweredNotification: 'TripHelpAnsweredNotification',
  TripHelpRequestedNotification: 'TripHelpRequestedNotification',
  TripNewProposalNotification: 'TripNewProposalNotification',
  TripNewVersionNotification: 'TripNewVersionNotification',
};

export const tripTabEnum = {
  DRAFTS: 'drafts',
  TRIPS: 'trips',
};
